import { CancellationToken } from 'infra/cancellation'
import { observable, action, makeObservable } from 'mobx'
import { Storage } from 'infra/storage'
import { fromEvent, merge, timer } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'

/**
 * Supported sounds.
 */
export enum SoundId {
  GotMessage,
  IncomingCall,
  RingingCall,
}

/**
 * Supported sounds.
 */
const sounds = new Map<SoundId, SoundSource>([
  [
    SoundId.GotMessage,
    {
      mp3: require('assets/sounds/got-message.mp3'),
      ogg: require('assets/sounds/got-message.ogg'),
    },
  ],
  [
    SoundId.IncomingCall,
    {
      mp3: require('assets/sounds/bell_ring.mp3'),
      ogg: require('assets/sounds/bell_ring.ogg'),
    },
  ],
  [
    SoundId.RingingCall,
    {
      mp3: require('assets/sounds/ringback_tone.mp3'),
      ogg: require('assets/sounds/ringback_tone.ogg'),
    },
  ],
])

/**
 * Sound player.
 */
export default class SoundPlayer {
  /**
   * Whether system sounds are enabled.
   */
  @observable isEnabled = false

  /**
   * We can only play sound once we have interacted with the document at least once.
   * We emit the first interaction.
   * @private
   */
  private readonly ready$ = merge(
    fromEvent(window, 'mousedown', { capture: true }),
    fromEvent(window, 'click', { capture: true }),
    fromEvent(window, 'keypress', { capture: true }),
    fromEvent(window, 'touchstart', { capture: true })
  ).pipe(
    // Unsubscribe after the first interaction.
    take(1),
    // Once we have the first one, just emit that on subscribe.
    shareLatest()
  )

  /**
   * Initializes the sound player.
   *
   * @param storage
   */
  constructor(private storage: Storage) {
    makeObservable(this)
    this.ready$.subscribe()
    this.setEnabled(storage.getItem('soundEnabled') !== 'disabled')
    toStream(() => this.isEnabled).subscribe((enabled) =>
      this.storage.setItem('soundEnabled', enabled ? 'enabled' : 'disabled')
    )
    // Initialize all audio objects
    for (const [sound] of sounds) {
      SoundPlayer.getAudio(sound)
    }
  }

  /**
   * Toggles whether the sound player is enabled.
   *
   * @param enabled
   */
  @action
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }

  /**
   * Play a sound.
   *
   * @param id
   */
  play(id: SoundId) {
    this.getSound(id).play(CancellationToken.default)
  }

  /**
   * Gets a sound by ID.
   *
   * @param id
   */
  getSound(id: SoundId): Sound {
    const play = (loop: boolean, ct: CancellationToken) => {
      if (!this.isEnabled) {
        return
      }

      const audio = SoundPlayer.getAudio(id)
      audio.loop = loop
      if (ct.isCancelled) {
        return
      }

      // Prevents some stupid DOMException, only way to do it.
      // When in doubt, setTimeout
      ct.onCancel(() => {
        audio.pause()
      })

      this.ready$
        .pipe(
          takeUntil(
            // If it takes more than 2 seconds to be ready,
            // just ignore it.
            merge(ct.asObservable(), timer(2000))
          ),
          take(1)
        )
        .subscribe(() => {
          audio.currentTime = 0
          audio.play()
        })
    }

    return {
      play: (ct: CancellationToken) => {
        play(false, ct)
      },
      playLoop: (ct: CancellationToken) => {
        play(true, ct)
      },
    }
  }

  /**
   * Gets the audio object for the specified sound.
   *
   * @param id
   */
  private static getAudio(id: SoundId) {
    const audio = new window.Audio()
    const type = audio.canPlayType('audio/mpeg') ? 'mp3' : 'ogg'
    audio.volume = 0.6
    audio.src = sounds.get(id)![type]
    audio.pause()
    return audio
  }
}

/**
 * Sound source.
 */
interface SoundSource {
  mp3: any
  ogg: any
}

/**
 * Sound controller.
 */
export interface Sound {
  /**
   * Plays the sound, stops when cancelled.
   *
   * @param ct
   */
  play(ct: CancellationToken): void

  /**
   * Plays the sound in a loop, stops when cancelled.
   * @param ct
   */
  playLoop(ct: CancellationToken): void
}
